import NeptuneElement from '../../NeptuneElement.js';
import '../../navigation/pagination/Pagination.js';

class SimpleDataTable extends NeptuneElement {
    templateMarkup = `
        <div>
            <div data-role="table-scroll-wrapper">
                <table>
                    <caption hidden>
                        <slot name="caption"></slot>
                    </caption>
                </table>
            </div>
            <neptune-pagination></neptune-pagination>
        </div>
    `;

    componentCssUrl = '/src/components/core/simpleDataTable/simple-data-table.css';

    #sortKey;
    #sortDirection;
    #currentPageSize;
    #currentPageSizeValue;

    constructor() {
        super();
        this.handleSortClick = this.handleSortClick.bind(this);
        this.handlePaginationChange = this.handlePaginationChange.bind(this);
    }

    static get observedAttributes() {
        return [
            'records',
            'settings'
        ];
    }

    async connectedCallback() {
        await super.connectedCallback();
        this.processInitialAttributesAfterRender(SimpleDataTable.observedAttributes);
        this.setEventListeners();
        this.setTable();
    }

    async disconnectedCallback() {
        this.removeEventListeners();
        await super.disconnectedCallback();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered || oldValue === newValue) {
             return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name) {
        switch (name) {
            case 'records':
            case 'settings':
                this.setTable();
                break;
            default:
                break;
        }
    }

    setEventListeners() {
        this.removeEventListeners();

        const table = this.shadowRoot?.querySelector('table');
        if (table) table.addEventListener('click', this.handleSortClick);

        const paginationElement = this.shadowRoot?.querySelector('neptune-pagination');
        if (paginationElement) {
            paginationElement.addEventListener('pagination-change', this.handlePaginationChange);
        }
    }

    removeEventListeners() {
        const table = this.shadowRoot?.querySelector('table');
        if (table) table.removeEventListener('click', this.handleSortClick);

        const paginationElement = this.shadowRoot?.querySelector('neptune-pagination');
        if (paginationElement) {
            paginationElement.removeEventListener('pagination-change', this.handlePaginationChange);
        }
    }

    getSettings() {
        try {
            const raw = this.getAttribute('settings');
            if (!raw) {
                return {};
            }

            const settings = JSON.parse(raw);
            if (typeof settings !== 'object' || settings === null || Array.isArray(settings)) {
                return {};
            }

            return settings;
        } catch {
            return {};
        }
    }

    getHeaders(settings = {}) {
        try {
            const rawHeaders = settings?.headers;
            if (!Array.isArray(rawHeaders)) {
                return [];
            }

            return rawHeaders.map((item, index) => {
                if ('string' === typeof item) {
                    return {
                        key: item,
                        label: item,
                        type: 'string'
                    };
                }

                const key = item?.key || item?.label || `col_${index}`;
                const label = item?.label || key;
                const type = item?.type || 'string';
                const width = this.normalizeColumnWidth(item?.width);

                return { key, label, type, width };
            });
        } catch {
            return [];
        }
    }

    normalizeColumnWidth(width) {
        if ('string' !== typeof width) return '';

        const value = width.trim().toLowerCase();
        if (!value) return '';

        // Allow valid CSS width values (ch).
        if (/^\d+(\.\d+)?ch$/.test(value)) {
            return value;
        }

        return '';
    }

    getDataRows() {
        try {
            const records = this.getAttribute('records');
            if (!records) {
                return [];
            }

            const parsed = JSON.parse(records);
            if (!Array.isArray(parsed)) {
                return [];
            }

            return parsed;
        } catch {
            return [];
        }
    }


    compareValues(a, b, type) {
        const valueA = a ?? '';
        const valueB = b ?? '';

        if (type === 'number') {
            const numberA = Number(valueA);
            const numberB = Number(valueB);
            if (Number.isNaN(numberA) && Number.isNaN(numberB)) return 0;
            if (Number.isNaN(numberA)) return 1;
            if (Number.isNaN(numberB)) return -1;
            return numberA - numberB;
        }

        if (type === 'date') {
            const dateA = new Date(valueA).getTime();
            const dateB = new Date(valueB).getTime();
            if (Number.isNaN(dateA) && Number.isNaN(dateB)) return 0;
            if (Number.isNaN(dateA)) return 1;
            if (Number.isNaN(dateB)) return -1;
            return dateA - dateB;
        }

        return new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }).compare(String(valueA), String(valueB));
    }

    sortRows(rows, columns, sortKey, sortDirection) {
        if (!sortKey) return rows;

        const column = columns.find((col) => col?.key === sortKey) || {};
        const type = column.type || 'string';

        return [...rows].sort((row1, row2) => {
            const result = this.compareValues(row1?.[sortKey], row2?.[sortKey], type);
            return sortDirection === 'desc' ? -result : result;
        });
    }

    clearTable(table) {
        const captionElement = table.querySelector('caption');
        table.textContent = '';
        if (captionElement) {
            table.appendChild(captionElement);
        }
    }

    renderCaption(table) {
        const captionElement = table.querySelector('caption');
        const captionSlot = captionElement?.querySelector('slot[name="caption"]');
        if (!captionElement || !captionSlot) return;

        const slotNodes = captionSlot.assignedNodes?.({ flatten: true }) || [];
        const hasCaptionContent = slotNodes.some((node) => {
            return (node.textContent || '') !== '';
        });

        captionElement.hidden = !hasCaptionContent;
    }

    renderColgroup(table, columns) {
        const hasAnyWidth = columns.some((column) => 'string' === typeof column?.width && column.width.trim() !== '');
        if (!hasAnyWidth) return;

        const colgroup = document.createElement('colgroup');

        for (const column of columns) {
            const col = document.createElement('col');
            const width = 'string' === typeof column?.width ? column.width : '';

            if (width) {
                col.style.setProperty('--neptune-simple-data-table-col-width', width);
            }

            colgroup.appendChild(col);
        }

        table.appendChild(colgroup);
    }

    renderHeader(table, columns, sortKey, sortDirection) {
        const thead = document.createElement('thead');
        const headRow = document.createElement('tr');

        for (const column of columns) {
            const th = document.createElement('th');
            th.setAttribute('scope', 'col');

            const key = column?.key || '';
            const label = column?.label ?? key;

            const labelSpan = document.createElement('span');
            labelSpan.textContent = String(label);
            th.appendChild(labelSpan);

            if (key) {
                th.setAttribute('data-sort-key', key);
            }

            if (sortKey && key === sortKey) {
                th.setAttribute('aria-sort', sortDirection === 'desc' ? 'descending' : 'ascending');

                const icon = document.createElement('i');
                icon.setAttribute('aria-hidden', 'true');
                icon.className = `fa-solid ${sortDirection === 'desc' ? 'fa-arrow-down' : 'fa-arrow-up'}`;
                th.appendChild(icon);
            }

            headRow.appendChild(th);
        }

        thead.appendChild(headRow);
        table.appendChild(thead);
    }

    updateCellTitleAttributes(table) {
        const tableCells = table.querySelectorAll('th, td');
        tableCells.forEach((cell) => {
            const contentElement = cell.querySelector('span') || cell;
            const cellText = (contentElement.textContent || '').trim();
            const isTruncated = contentElement.scrollWidth > contentElement.clientWidth;

            if (cellText && isTruncated) {
                cell.setAttribute('title', cellText);
            } else {
                cell.removeAttribute('title');
            }
        });
    }

    renderBody(table, columns, rows, pageSize = 0) {
        const tbody = document.createElement('tbody');

        for (const row of rows) {
            const tableRow = document.createElement('tr');

            for (const column of columns) {
                const key = column?.key;
                const value = key ? row?.[key] : '';
                const cell = document.createElement('td');
                cell.textContent = value == null ? '' : String(value);
                tableRow.appendChild(cell);
            }

            tbody.appendChild(tableRow);
        }

        const emptyRowsNeeded = pageSize > 0 ? Math.max(0, pageSize - rows.length) : 0;

        for (let i = 0; i < emptyRowsNeeded; i++) {
            const emptyRow = document.createElement('tr');
            emptyRow.setAttribute('aria-hidden', 'true');

            for (const column of columns) {
                emptyRow.appendChild(document.createElement('td'));
            }

            tbody.appendChild(emptyRow);
        }

        table.appendChild(tbody);
    }

    validateHeadersAgainstRecords(columns, rows) {
        if (!Array.isArray(columns) || columns.length === 0 || !Array.isArray(rows) || rows.length === 0) {
            return;
        }

        const missingKeys = columns
            .map((column) => column?.key)
            .filter((key) => 'string' === typeof key && key.trim() !== '')
            .filter((key) => rows.every((row) => !(key in (row || {}))));

        if (missingKeys.length > 0) {
            throw new Error(`simple-data-table: Header keys not found in records: ${missingKeys.join(', ')}`);
        }
    }

    setTable(page = 1, pageSize = 10, pageSizeValue = '10') {
        const table = this.shadowRoot?.querySelector('table');
        if (!table) return;

        const settings = this.getSettings();
        const columns = this.getHeaders(settings);
        const allRows = this.getDataRows();

        this.validateHeadersAgainstRecords(columns, allRows);

        const sortKey = this.#sortKey;
        const sortDirection = this.#sortDirection ?? 'asc';
        const baseRows = this.sortRows(allRows, columns, sortKey, sortDirection);
        const hasExplicitTotal = Object.prototype.hasOwnProperty.call(settings, 'totalNumberOfRecords');
        const totalFromSettings = Number(settings?.totalNumberOfRecords);
        const hasValidExplicitTotal = Number.isFinite(totalFromSettings) && totalFromSettings >= 0;
        const totalItems = hasValidExplicitTotal ? Math.floor(totalFromSettings) : allRows.length;

        let visibleRows = baseRows;
        if (pageSizeValue !== 'all') {
            const startIndex = (page - 1) * pageSize;
            visibleRows = baseRows.slice(startIndex, startIndex + pageSize);
        }

        let bodyPageSize = 0;
        if (pageSizeValue !== 'all' && pageSize > 0) {
            const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
            const isLastPage = page >= totalPages;
            bodyPageSize = isLastPage ? pageSize : 0;
        }

        this.clearTable(table);
        this.renderCaption(table);
        this.renderColgroup(table, columns);
        this.renderHeader(table, columns, sortKey, sortDirection);
        this.renderBody(table, columns, visibleRows, bodyPageSize);
        this.updateCellTitleAttributes(table);

        const paginationElement = this.shadowRoot?.querySelector('neptune-pagination');
        if (paginationElement) {
            const shouldShowPagination = totalItems > 10;

            paginationElement.hidden = !shouldShowPagination;

            if (hasExplicitTotal && hasValidExplicitTotal) {
                paginationElement.setAttribute('total-items', String(totalItems));
            } else {
                paginationElement.removeAttribute('total-items');
            }

            if (typeof paginationElement.syncState === 'function') {
                paginationElement.syncState({ page, pageSize, pageSizeValue, totalItems });
            }
        }

        this.#applySettingsFlags(settings);
    }

    handleSortClick(event) {
        const sortableHeader = event.target?.closest?.('th[data-sort-key]');
        if (!sortableHeader) return;

        const nextSortKey = sortableHeader.getAttribute('data-sort-key');
        if (!nextSortKey) return;

        if (this.#sortKey === nextSortKey) {
            this.#sortDirection = this.#sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.#sortKey = nextSortKey;
            this.#sortDirection = 'asc';
        }

        const currentPageSize = this.#currentPageSize ?? 10;
        const currentPageSizeValue = this.#currentPageSizeValue ?? '10';
        this.setTable(1, currentPageSize, currentPageSizeValue);
    }

    handlePaginationChange(event) {
        const { page, pageSize, pageSizeValue } = event.detail || {};
        const nextPage = Number.isFinite(page) ? page : 1;
        const nextPageSize = Number.isFinite(pageSize) ? pageSize : 10;
        const nextPageSizeValue = pageSizeValue || String(nextPageSize);

        this.#currentPageSize = nextPageSize;
        this.#currentPageSizeValue = nextPageSizeValue;
        this.setTable(nextPage, nextPageSize, nextPageSizeValue);
    }

    #applySettingsFlags(settings) {
        const hasBorder = settings?.hasBorder !== false;
        const isStriped = settings?.isStriped !== false;

        if (hasBorder) {
            this.setAttribute('has-divider', '');
        } else {
            this.removeAttribute('has-divider');
        }

        if (isStriped) {
            this.setAttribute('is-zebra-striped', '');
        } else {
            this.removeAttribute('is-zebra-striped');
        }
    }
}

customElements.define('neptune-table', SimpleDataTable);
export default SimpleDataTable;